// src/pages/gt3x/Briefing.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import CustomSelect from "../../components/CustomSelect.jsx";
import { useAuth } from "../../auth/AuthProvider.jsx";
import { hasRole } from "../../auth/roles.js";
import {
    fetchAdminRaceBriefing,
    fetchPublicRaceBriefing,
    saveAdminRaceBriefing,
} from "../../lib/managerApi.js";

const SERIES_KEY = "gt3x";
const SEASON_KEY = "active";

const EMPTY_BRIEFING = {
    season_id: "",
    race_id: "",

    ingame_date: "",

    practice_ingame_datetime: "",

    qualy_ingame_datetime: "",

    warmup_ingame_datetime: "",

    race_ingame_datetime: "",

    briefing_text: "",
    qualy_bans_text: "-",
    race_bans_text: "-",
};

const SESSION_META = [
    {
        key: "practice",
        label: "Practice",
        accent: "pill--team",
        timeField: "practice_ingame_datetime",
    },
    {
        key: "qualy",
        label: "Qualy",
        accent: "pill--int",
        timeField: "qualy_ingame_datetime",
    },
    {
        key: "warmup",
        label: "Warmup",
        accent: "pill--gesamt",
        timeField: "warmup_ingame_datetime",
    },
    {
        key: "race",
        label: "Race",
        accent: "pill--am",
        timeField: "race_ingame_datetime",
    },
];

function prettyError(err) {
    if (!err) return "Unbekannter Fehler";
    if (typeof err === "string") return err;
    if (err instanceof Error) return err.message || "Fehler";
    if (err.detail) return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
    return String(err);
}

function formatRaceDate(value) {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return date.toLocaleString("de-DE", {
        timeZone: "Europe/Berlin",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function raceLabel(race) {
    if (!race) return "Rennen auswählen";

    const title = race.race_title || race.track_name || "Rennen";
    const date = race.race_date ? ` · ${formatRaceDate(race.race_date)}` : "";

    return `${race.race_tag || `R${race.race_no}`} · ${title}${date}`;
}

function normaliseBriefingForForm(briefing, fallback = {}) {
    const source = briefing || {};

    return {
        ...EMPTY_BRIEFING,

        season_id: source.season_id || fallback.season_id || "",
        race_id: source.race_id || fallback.race_id || "",

        ingame_date: source.ingame_date || "",

        practice_ingame_datetime: source.practice_ingame_datetime || "",

        qualy_ingame_datetime: source.qualy_ingame_datetime || "",

        warmup_ingame_datetime: source.warmup_ingame_datetime || "",

        race_ingame_datetime: source.race_ingame_datetime || "",

        briefing_text: source.briefing_text || "",
        qualy_bans_text: source.qualy_bans_text || "-",
        race_bans_text: source.race_bans_text || "-",
    };
}

function StatusMessage({ type = "ok", children }) {
    if (!children) return null;

    return (
        <div className={type === "error" ? "rv-message rv-message--error" : "rv-message rv-message--ok"}>
            {children}
        </div>
    );
}

function SessionCard({ meta, briefing }) {
    const timeValue = briefing?.[meta.timeField] || "–";

    return (
        <article
            className="card"
            style={{
                display: "grid",
                gap: 10,
                minHeight: 128,
                background: "linear-gradient(180deg, rgba(255,255,255,.045), rgba(255,255,255,.025))",
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                <span className={`pill ${meta.accent}`}>{meta.label}</span>
            </div>

            <div>
                <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
                    In-Game Uhrzeit
                </div>
                <strong style={{ fontSize: 17 }}>{timeValue}</strong>
            </div>

        </article>
    );
}

function SessionEditor({ meta, form, onChange }) {
    return (
        <div
            style={{
                display: "grid",
                gap: 10,
                padding: 12,
                borderRadius: 14,
                border: "1px solid rgba(255,255,255,.10)",
                background: "rgba(255,255,255,.035)",
            }}
        >
            <span className={`pill ${meta.accent}`} style={{ width: "fit-content" }}>
                {meta.label}
            </span>

            <label style={{ display: "grid", gap: 5 }}>
                <span className="muted" style={{ fontSize: 12 }}>In-Game Uhrzeit</span>
                <input
                    className="input"
                    value={form[meta.timeField] || ""}
                    placeholder="z. B. 14:45"
                    onChange={(event) => onChange(meta.timeField, event.target.value)}
                />
            </label>

        </div>
    );
}

function BriefingEditor({
                            canEdit,
                            season,
                            selectedRace,
                            form,
                            onChange,
                            onSave,
                            saving,
                        }) {
    if (!canEdit || !season || !selectedRace) return null;

    return (
        <section
            className="card card--accent"
            style={{
                display: "grid",
                gap: 16,
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div>
                    <div className="rv-page-kicker">Admin / Orga</div>
                    <h2 style={{ margin: "4px 0 0" }}>Briefing bearbeiten</h2>
                    <p className="muted" style={{ marginTop: 6 }}>
                        Inhalte für {raceLabel(selectedRace)} pflegen. Fahrer sehen nach dem Speichern direkt die aktuelle Version.
                    </p>
                </div>

                <span className="pill pill--role-orga">Editor</span>
            </div>
            <label style={{ display: "grid", gap: 6, maxWidth: 360 }}>
                <span className="muted">In-Game Datum</span>
                <input
                    className="input"
                    value={form.ingame_date || ""}
                    placeholder="z. B. 2026-07-14"
                    onChange={(event) => onChange("ingame_date", event.target.value)}
                />
            </label>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 12,
                }}
            >
                {SESSION_META.map((meta) => (
                    <SessionEditor
                        key={meta.key}
                        meta={meta}
                        form={form}
                        onChange={onChange}
                    />
                ))}
            </div>

            <label style={{ display: "grid", gap: 6 }}>
                <span className="muted">Briefingtext</span>
                <textarea
                    className="input"
                    value={form.briefing_text || ""}
                    placeholder="Briefingtext für Fahrer eintragen..."
                    rows={8}
                    onChange={(event) => onChange("briefing_text", event.target.value)}
                    style={{
                        resize: "vertical",
                        minHeight: 180,
                        lineHeight: 1.55,
                    }}
                />
            </label>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                    gap: 12,
                }}
            >
                <label style={{ display: "grid", gap: 6 }}>
                    <span className="muted">Qualysperren</span>
                    <textarea
                        className="input"
                        value={form.qualy_bans_text || ""}
                        placeholder="-"
                        rows={4}
                        onChange={(event) => onChange("qualy_bans_text", event.target.value)}
                        style={{ resize: "vertical" }}
                    />
                </label>

                <label style={{ display: "grid", gap: 6 }}>
                    <span className="muted">Rennsperren</span>
                    <textarea
                        className="input"
                        value={form.race_bans_text || ""}
                        placeholder="-"
                        rows={4}
                        onChange={(event) => onChange("race_bans_text", event.target.value)}
                        style={{ resize: "vertical" }}
                    />
                </label>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                    type="button"
                    className="btn btn--accent"
                    disabled={saving}
                    onClick={onSave}
                >
                    {saving ? "Speichere…" : "Briefing speichern"}
                </button>
            </div>
        </section>
    );
}

function BriefingDisplay({ briefing, selectedRace }) {
    const hasBriefingText = Boolean(String(briefing?.briefing_text || "").trim());

    return (
        <>
            <section className="card" style={{ display: "grid", gap: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <div>
                        <div className="rv-page-kicker">Race Briefing</div>
                        <h2 style={{ margin: "4px 0 0" }}>
                            {selectedRace ? raceLabel(selectedRace) : "Rennen"}
                        </h2>

                        {briefing?.ingame_date && (
                            <div className="muted" style={{ marginTop: 6 }}>
                                In-Game Datum: <strong>{briefing.ingame_date}</strong>
                            </div>
                        )}
                    </div>

                    <span className="pill pill--int">
                        Sessiondaten
                     </span>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: 12,
                    }}
                >
                    {SESSION_META.map((meta) => (
                        <SessionCard
                            key={meta.key}
                            meta={meta}
                            briefing={briefing}
                        />
                    ))}
                </div>
            </section>

            <section className="card" style={{ display: "grid", gap: 12 }}>
                <div>
                    <div className="rv-page-kicker">Informationen</div>
                    <h2 style={{ margin: "4px 0 0" }}>Briefingtext</h2>
                </div>

                {hasBriefingText ? (
                    <div
                        style={{
                            whiteSpace: "pre-wrap",
                            lineHeight: 1.65,
                            color: "var(--text)",
                            fontFamily: "var(--ff-Everett), var(--ff-Manrope), sans-serif",
                        }}
                    >
                        {briefing.briefing_text}
                    </div>
                ) : (
                    <p className="muted">
                        Für dieses Rennen ist aktuell noch kein Briefingtext hinterlegt.
                    </p>
                )}
            </section>

            <section
                className="card"
                style={{
                    display: "grid",
                    gap: 12,
                    borderColor: "rgba(255,212,112,.22)",
                    background: "linear-gradient(180deg, rgba(255,212,112,.055), rgba(255,255,255,.025))",
                }}
            >
                <div>
                    <div className="rv-page-kicker">Sperren</div>
                    <h2 style={{ margin: "4px 0 0" }}>Qualy- & Rennsperren</h2>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                        gap: 12,
                    }}
                >
                    <div
                        style={{
                            padding: 14,
                            borderRadius: 14,
                            border: "1px solid rgba(255,255,255,.10)",
                            background: "rgba(255,255,255,.035)",
                        }}
                    >
                        <span className="pill pill--int">Qualysperren</span>
                        <div style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>
                            {briefing?.qualy_bans_text || "-"}
                        </div>
                    </div>

                    <div
                        style={{
                            padding: 14,
                            borderRadius: 14,
                            border: "1px solid rgba(255,255,255,.10)",
                            background: "rgba(255,255,255,.035)",
                        }}
                    >
                        <span className="pill pill--pro">Rennsperren</span>
                        <div style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>
                            {briefing?.race_bans_text || "-"}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

export default function Briefing() {
    const { user } = useAuth() || {};

    const canEdit = Boolean(
        hasRole(user, "admin") ||
        hasRole(user, "orga")
    );

    const [season, setSeason] = useState(null);
    const [races, setRaces] = useState([]);
    const [selectedRaceId, setSelectedRaceId] = useState("");

    const [selectedRace, setSelectedRace] = useState(null);
    const [briefing, setBriefing] = useState(EMPTY_BRIEFING);
    const [form, setForm] = useState(EMPTY_BRIEFING);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const raceOptions = useMemo(() => {
        return races.map((race) => ({
            value: String(race.id),
            label: raceLabel(race),
        }));
    }, [races]);

    const loadBriefing = useCallback(async (raceId = "") => {
        setLoading(true);
        setError("");

        try {
            const data = await fetchPublicRaceBriefing({
                seriesKey: SERIES_KEY,
                seasonKey: SEASON_KEY,
                raceId,
            });

            const nextSeason = data.season || null;
            const nextRaces = Array.isArray(data.races) ? data.races : [];
            const nextRace = data.race || null;
            const nextBriefing = normaliseBriefingForForm(data.briefing, {
                season_id: nextSeason?.id,
                race_id: nextRace?.id,
            });

            setSeason(nextSeason);
            setRaces(nextRaces);
            setSelectedRace(nextRace);
            setBriefing(nextBriefing);
            setForm(nextBriefing);

            if (nextRace?.id) {
                setSelectedRaceId(String(nextRace.id));
            }
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadBriefing("");
    }, [loadBriefing]);

    async function handleRaceChange(nextRaceId) {
        setSelectedRaceId(String(nextRaceId || ""));
        setSuccess("");
        await loadBriefing(nextRaceId);
    }

    function updateForm(key, value) {
        setForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    }

    async function saveBriefing() {
        if (!canEdit) return;

        if (!season?.id || !selectedRace?.id) {
            setError("Bitte zuerst eine Saison und ein Rennen laden.");
            return;
        }

        setSaving(true);
        setError("");
        setSuccess("");

        try {
            const payload = {
                ...form,
                season_id: Number(season.id),
                race_id: Number(selectedRace.id),

                qualy_bans_text: form.qualy_bans_text?.trim() || "-",
                race_bans_text: form.race_bans_text?.trim() || "-",
            };

            const saved = await saveAdminRaceBriefing(payload);
            const nextBriefing = normaliseBriefingForForm(saved.briefing, {
                season_id: season.id,
                race_id: selectedRace.id,
            });

            setBriefing(nextBriefing);
            setForm(nextBriefing);
            setSuccess("Briefing wurde gespeichert.");
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="rv-page briefing-page">
            <section className="rv-page-hero">
                <div className="rv-page-hero__inner">
                    <div>
                        <div className="rv-page-kicker">GT3 Xperience</div>
                        <h1 className="rv-page-title">Briefing</h1>
                        <p className="rv-page-subtitle">
                            Aktuelle Renninformationen, In-Game Zeiten und Sperren.
                        </p>
                    </div>
                </div>
            </section>

            {error && <StatusMessage type="error">{error}</StatusMessage>}
            {success && <StatusMessage>{success}</StatusMessage>}

            <section className="card" style={{ display: "grid", gap: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <div>
                        <div className="rv-page-kicker">Auswahl</div>
                        <h2 style={{ margin: "4px 0 0" }}>Rennen auswählen</h2>
                    </div>

                    {selectedRace?.race_tag && (
                        <span className="pill pill--gesamt">
                            {selectedRace.race_tag}
                        </span>
                    )}
                </div>

                <div style={{ maxWidth: 520 }}>
                    <CustomSelect
                        value={selectedRaceId}
                        onChange={handleRaceChange}
                        options={raceOptions}
                        allowEmpty={false}
                        disabled={loading || !raceOptions.length}
                        placeholder={loading ? "Lade Briefing…" : "Rennen auswählen"}
                    />
                </div>

                <p className="muted" style={{ margin: 0 }}>
                    Standardmäßig wird das aktuell relevante Rennen anhand des Schedules ausgewählt.
                </p>
            </section>

            <BriefingEditor
                canEdit={canEdit}
                season={season}
                selectedRace={selectedRace}
                form={form}
                onChange={updateForm}
                onSave={saveBriefing}
                saving={saving}
            />

            {loading ? (
                <section className="card">
                    <div className="rv-page-kicker">Laden</div>
                    <h2>Briefing wird geladen…</h2>
                    <p className="muted">Die Daten werden aus der RaceVision-Datenbank geladen.</p>
                </section>
            ) : (
                <BriefingDisplay
                    briefing={briefing}
                    selectedRace={selectedRace}
                />
            )}
        </div>
    );
}
